import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { ActionIcon } from "@mantine/core";
import { useState } from "react";

import { Button, HeartIcon, HeartOutlineIcon } from "@/components/ui/elements";

import { infoMessage } from "@/i18n";
import { showNotification } from "@/util";
import {
  displayedFavoriteGuideMutation,
  followOfficeMutation,
  unfollowOfficeMutation,
  viewerFavoriteGuideStatusQuery,
} from "./_graphql";
import { FavoriteGuideModal } from "./FavoriteGuideModal";

type Props = {
  clientOfficeId: string;
  isFavorite: boolean;
  iconOnly?: boolean;
  /** 求人詳細フッター内（暗い背景上）の表示 */
  inFooter?: boolean;
  /** AppBar・施設名横など、未登録時アウトラインを teal にする */
  tealOutline?: boolean;
  /** お気に入りガイド案内の対象画面かどうか（求人詳細画面のみ true） */
  enableFavoriteGuide?: boolean;
};

export function OfficeFollowButton({
  clientOfficeId,
  isFavorite,
  iconOnly = false,
  inFooter = false,
  tealOutline = false,
  enableFavoriteGuide = false,
}: Props) {
  const [guideModalOpened, setGuideModalOpened] = useState(false);
  const client = useApolloClient();
  useQuery(viewerFavoriteGuideStatusQuery, { skip: !enableFavoriteGuide });
  const [displayedFavoriteGuide] = useMutation(displayedFavoriteGuideMutation);

  const [addMutation, { loading: addLoading }] = useMutation(
    followOfficeMutation,
    {
      refetchQueries: [
        "Bookmarks",
        "JobOfferFeed",
        "JobOffer",
        "OfficeJobOfferList",
      ],
    },
  );
  const [removeMutation, { loading: removeLoading }] = useMutation(
    unfollowOfficeMutation,
    {
      refetchQueries: [
        "Bookmarks",
        "JobOfferFeed",
        "JobOffer",
        "OfficeJobOfferList",
      ],
    },
  );

  const handleAddFavorite = async () => {
    await addMutation({
      variables: {
        input: {
          clientOfficeId: clientOfficeId,
        },
      },
      onError: (error) =>
        showNotification({
          type: "error",
          message: error.message,
        }),
      onCompleted: (data) => {
        const res = data.followOffice;
        if (!res) return;
        showNotification({
          type: "info",
          message: infoMessage("addOfficeFavorite"),
        });
        if (!enableFavoriteGuide || !res.firstFavorite) return;
        const guideStatus = client.readQuery({
          query: viewerFavoriteGuideStatusQuery,
        });
        if (guideStatus && !guideStatus.viewer.favoriteGuideDisplayed) {
          setGuideModalOpened(true);
        }
      },
    });
  };

  const handleGuideModalClose = () => {
    displayedFavoriteGuide({
      variables: { input: {} },
      onError: (error) =>
        showNotification({
          type: "error",
          message: error.message,
        }),
    });
    setGuideModalOpened(false);
  };
  const handleremoveFavorite = async () => {
    await removeMutation({
      variables: {
        input: {
          clientOfficeId: clientOfficeId,
        },
      },
      onError: (error) =>
        showNotification({
          type: "error",
          message: error.message,
        }),
      onCompleted: (data) => {
        const res = data.unfollowOffice;
        if (!res) return;
        showNotification({
          type: "info",
          message: infoMessage("removeOfficeFavorite"),
        });
      },
    });
  };
  const isLoading = addLoading || removeLoading;

  if (iconOnly) {
    const favoriteIconColor =
      inFooter || tealOutline ? "var(--mantine-color-red-6)" : "red";
    const outlineIconColor = inFooter
      ? "white"
      : tealOutline
        ? "var(--mantine-color-teal-6)"
        : undefined;

    return (
      <>
        <ActionIcon
          variant={inFooter || tealOutline ? "transparent" : "subtle"}
          size="lg"
          loading={isLoading}
          onClick={isFavorite ? handleremoveFavorite : handleAddFavorite}
          aria-label={isFavorite ? "お気に入り解除" : "お気に入りに追加"}
        >
          {isFavorite ? (
            <HeartIcon color={favoriteIconColor} size="1.2em" />
          ) : (
            <HeartOutlineIcon color={outlineIconColor} size="1.2em" />
          )}
        </ActionIcon>
        <FavoriteGuideModal
          opened={guideModalOpened}
          close={handleGuideModalClose}
        />
      </>
    );
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        loading={isLoading}
        onClick={isFavorite ? handleremoveFavorite : handleAddFavorite}
        rightSection={
          isFavorite ? (
            <HeartIcon color="red" size="1.5em" />
          ) : (
            <HeartOutlineIcon size="1.5em" />
          )
        }
      >
        {isFavorite ? "お気に入り解除" : "お気に入り"}
      </Button>
      <FavoriteGuideModal
        opened={guideModalOpened}
        close={handleGuideModalClose}
      />
    </>
  );
}
